import { Box, Grid, useTheme, SxProps, Alert, Stack } from '@mui/material';
import React, { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CAppTitle from '../../../app/components/CAppTitle';
import { useAuthContext } from '../../auth/contexts/AuthContext';
import { Helmet } from 'react-helmet';
import langUserProfile from '../langs/langUserProfile';
import { useAppNavigationContext } from '../../../app/features/navigation/providers/AppNavigationProvider';
import VUserProfilePersonalData from '../views/user-profile/VUserProfilePersonalData';
import VUserProfilePassword from '../views/user-profile/VUserProfilePassword';
import VUserProfileSecurityFactors from '../views/user-profile/VUserProfileSecurityFactors';
import VUserProfileSettings from '../views/user-profile/VUserProfileSettings';
import VUserProfileNotifications from '../views/user-profile/VUserProfileNotifications';
import VUserProfileInformation from '../views/user-profile/VUserProfileInformation';
import IUserProfile from '../interfaces/IUserProfile';
import useUserProfile from '../hooks/useUserProfile';
import useInvestFunds from '../../investor/hooks/fund/useInvestFunds';

const PUserProfile = () => {
	const { t } = useTranslation();
	const theme = useTheme();
	const { investorProfile } = useUserProfile();
	const { funds } = useInvestFunds();

	const navigationContext = useAppNavigationContext();
	useEffect(() => {
		if (navigationContext.displayCurrencySelector === true) {
			navigationContext.setDisplayCurrencySelector(false);
		}
	}, [navigationContext]);

	const { currentCompany, authContext } = useAuthContext();

	const [disableEdit, setDisableEdit] = useState(true);
	useEffect(() => {
		if (authContext === 'investor') {
			setDisableEdit(false);
		} else {
			setDisableEdit(true);
		}
	}, [authContext]);

	const [profileData, setProfileData] = useState<IUserProfile | null>(null);

	useEffect(() => {
		if (investorProfile) {
			setProfileData({
				...investorProfile,
			});
		}
	}, [investorProfile]);

	const styleTitle = {
		color: theme.palette.primary.main,
		verticalAlign: 'text-bottom',
		boxSizing: 'inherit',
		textAlign: 'left',
		alignItems: 'center',
		display: 'inline-flex',
		fontWeight: 600,
		fontSize: '1.25rem',
		'& .icon': {
			marginRight: '10px',
			fontSize: '1.8rem',
		},
	};
	const sxStyle = {
		padding: '30px 30px 50px 30px',

		border: '0px',
		borderRadius: '10px',
		textAlign: 'left',
		'& .title': styleTitle,
	} as SxProps;

	const sxSelect = {
		border: '1px solid ',
		borderColor: theme.palette.primary.main,
		color: theme.palette.primary.main,
	} as SxProps;

	const sxEditButton = {
		padding: '5px 12px',
		fontSize: '0.85rem',
		lineHeight: '1',
		margin: 0,
		boxShadow: 'none!important',
	};

	const sxTextField = {
		//borderRadius: '10px',
		'& .MuiInputBase-root': {
			border: '1px solid',
			borderRadius: '5px',
			'& .MuiOutlinedInput-notchedOutline': {
				display: 'block',
			},
		},

		borderColor: theme.palette.primary.main,
	};

	return (
		<>
			{profileData ? (
				<>
					<Helmet>
						<title>
							{authContext === 'employee' ? '[E] ' : ''} {t(langUserProfile.title_my_profile.tag)} -{' '}
							{profileData.firstName} {profileData.lastName} - {currentCompany ? currentCompany.name : ''}
						</title>
					</Helmet>
					<CAppTitle title={t(langUserProfile.title_my_profile.tag)} id="profile-title" />
					{disableEdit ? (
						<Alert severity="info" sx={{ marginBottom: '15px' }}>
							{t(langUserProfile.alert_info_disable_edit_for_employee.tag)}
						</Alert>
					) : (
						''
					)}
					<Grid container spacing={3} alignItems="top" justifyItems="center">
						<Grid item sm={6} xs={12}>
							<Stack spacing={3}>
								<VUserProfileInformation
									sxEditButton={sxEditButton}
									sxStyle={sxStyle}
									sxTextField={sxTextField}
									disableEdit={disableEdit}
								/>
								{profileData?.features?.emailNotificationEnabled &&
								funds &&
								funds.some((x) => x.typeTag === 'private_credit') ? (
									<VUserProfileNotifications
										sxEditButton={sxEditButton}
										sxStyle={sxStyle}
										disableEdit={disableEdit}
									/>
								) : (
									<></>
								)}
								<VUserProfilePassword
									sxEditButton={sxEditButton}
									sxStyle={sxStyle}
									sxTextField={sxTextField}
									disableEdit={disableEdit}
								/>
							</Stack>
						</Grid>
						<Grid item sm={6} xs={12}>
							<Stack spacing={3}>
								<VUserProfileSettings
									sxEditButton={sxEditButton}
									sxStyle={sxStyle}
									sxSelect={sxSelect}
									disableEdit={disableEdit}
								/>
								<VUserProfileSecurityFactors
									sxStyle={sxStyle}
									sxEditButton={sxEditButton}
									disableEdit={disableEdit}
								/>
								<VUserProfilePersonalData sxStyle={sxStyle} />
							</Stack>
						</Grid>
					</Grid>
				</>
			) : (
				''
			)}
		</>
	);
};
type FieldBoxProps = {
	label?: string | ReactNode;
	value: string | ReactNode;
	bEdit: boolean;
	component?: ReactNode;
	readonly?: boolean;
};
export const FieldBox = (props: FieldBoxProps) => {
	const { label, value, bEdit, component, readonly } = props;
	const theme = useTheme();
	return (
		<Box
			sx={{
				margin: '24px 0px 0px 0px',
				'& .label': {
					fontSize: '1rem',
					lineHeight: '1.5',
					marginBottom: '5px',
					fontWeight: 600,
					color: theme.palette.primary.main,
				},
				'& .value': {
					fontSize: '0.9rem',
					lineHeight: '1.5',
					fontWeight: 400,
					color: theme.palette.primary.main,
				},
			}}
		>
			<Box className="label">{label}</Box>
			<Box className="value">{bEdit && !readonly ? component : value}</Box>
		</Box>
	);
};

FieldBox.defaultProps = {
	bEdit: false,
	readonly: false,
};

export default PUserProfile;
